// src/HRMS Site/api/backendApi.js
// ──────────────────────────────────────────────────────────────
//  WebIdeal HRMS API Client, connects the HRMS UI to HRMS_Backend
//  (the standalone Express/PostgreSQL service in ../HRMS_Backend).
//
//  This is intentionally separate from the shared jobs/recruit client
//  (src/api/backendApi.js) because HRMS runs against its own backend and
//  its own auth token. Configure the base URL with REACT_APP_HRMS_API_URL
//  (no trailing slash); falls back to localhost:3000 for local dev.
// ──────────────────────────────────────────────────────────────

const API_ROOT = (process.env.REACT_APP_HRMS_API_URL || "http://localhost:3000").replace(/\/+$/, "");
const API_BASE = `${API_ROOT}/api`;

// ──────────────────────────────────────────────
//  SESSION STATE
//
//  The access token lives ONLY here, in memory, never in localStorage or
//  sessionStorage. Both of those are plain JS-readable storage, so anything
//  that can run a script on this page (an XSS bug, a malicious dependency,
//  a browser extension) can read a token sitting in them. Keeping it in a
//  module-level variable means it simply doesn't exist anywhere a script
//  could read it "at rest", it's gone the moment the tab closes.
//
//  "Remember me" is no longer this file's job at all. It's now entirely a
//  server-side decision: the backend hands back an httpOnly refresh-token
//  cookie (unreadable by any JS, ours or an attacker's) that's persistent
//  when "remember me" was checked and session-only when it wasn't. This
//  file's only role is to exchange that cookie for a fresh access token, //  see restoreSession()/silentRefresh() below.
// ──────────────────────────────────────────────
let accessToken = null;
let currentUser = null;
// The caller's permission keys, resolved server-side from their role and
// shipped by /auth/me. The admin console gates its own controls off this
// same list the API enforces, instead of re-deriving rules from the role.
let currentPermissions = [];

function setSession(token, user) {
  accessToken = token || null;
  currentUser = user || null;
}

function clearSession() {
  accessToken = null;
  currentUser = null;
  currentPermissions = [];
}

// Normalize the user shape so components can always read `full_name`/`name`.
function normalizeUser(raw) {
  if (!raw) return null;
  const full_name = raw.full_name || raw.name || raw.display_name || "";
  return {
    ...raw,
    name: raw.name || full_name,
    full_name: full_name || raw.name || "",
  };
}

// ──────────────────────────────────────────────
//  CORE REQUEST
// ──────────────────────────────────────────────

// `credentials: "include"` is required on every call: it's what makes the
// browser attach the httpOnly refresh-token cookie to same-site requests to
// the API (and the backend must echo back a specific CORS origin + allow
// credentials for this to work cross-origin, which it now does).
async function rawRequest(path, options = {}) {
  const headers = {
    ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: "include",
    // Prevent browser from caching API responses (avoids stale 304s for
    // endpoints like /attendance/today that change between tab opens).
    cache: "no-store",
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const error = new Error(data.error || data.message || `API error: ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // Some endpoints (rare) may return no body.
  return res.status === 204 ? null : res.json();
}

// Exchanges the httpOnly refresh cookie for a new access token. Called once
// at app boot (restoreSession) and automatically mid-session whenever an
// access token has expired, this is the entire "stay logged in" mechanism.
// If there's no valid refresh token (never logged in, expired, revoked,
// "remember me" wasn't checked and the browser session ended) this simply
// resolves to null; it never throws.
//
// IMPORTANT: the backend refresh token is single-use (rotated on every call,
// see Backend/src/repositories/refreshToken.repository.js#rotate). If two
// callers invoke silentRefresh() at the same moment, e.g. React StrictMode
// mounting an effect twice in dev, or two parallel API calls both getting a
// 401 right after the access token expires, they'd both send the *same*
// cookie value to /auth/refresh. Only the first would win; the backend would
// see the second as reuse of an already-rotated token (its theft-detection
// signal) and revoke the whole session, silently logging the user out even
// though nothing was actually stolen. `refreshPromise` collapses concurrent
// callers onto a single in-flight request so this race can't happen.
let refreshPromise = null;
async function silentRefresh() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const raw = await rawRequest("/auth/refresh", { method: "POST" });
      const token = raw?.token;
      const user = normalizeUser(raw?.user);
      if (!token) return null;
      setSession(token, user);
      return user;
    } catch {
      clearSession();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Wraps rawRequest with one automatic retry-after-refresh: if the access
// token expired mid-session (401), silently refresh it and replay the
// request exactly once. The rest of the app never has to think about token
// expiry at all.
async function apiRequest(path, options = {}) {
  try {
    return await rawRequest(path, options);
  } catch (err) {
    if (err.status === 401 && accessToken) {
      const user = await silentRefresh();
      if (user) {
        return rawRequest(path, options);
      }
    }
    throw err;
  }
}

// Same as rawRequest, but also reads the X-Total-Count response header and
// returns `{ rows, total }`. Server-side pagination needs the grand total
// (for "1–25 of 342"), which cannot travel in the JSON body without
// changing every list endpoint's shape — so the backend sends it as a
// header and exposes it through CORS (see exposedHeaders in app.js).
async function rawRequestWithTotal(path, options = {}) {
  const headers = {
    ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: "include",
    cache: "no-store",
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const error = new Error(data.error || data.message || `API error: ${res.status}`);
    error.status = res.status;
    throw error;
  }

  const body = res.status === 204 ? null : await res.json();
  const rows = Array.isArray(body) ? body : body?.rows || [];
  // Fall back to the row count when the header is absent (an endpoint that
  // doesn't paginate), so callers always get a usable number.
  const headerTotal = res.headers.get("X-Total-Count");
  const total = headerTotal != null ? parseInt(headerTotal, 10) : rows.length;

  return { rows, total: Number.isNaN(total) ? rows.length : total };
}

async function apiRequestWithTotal(path, options = {}) {
  try {
    return await rawRequestWithTotal(path, options);
  } catch (err) {
    if (err.status === 401 && accessToken) {
      const user = await silentRefresh();
      if (user) {
        return rawRequestWithTotal(path, options);
      }
    }
    throw err;
  }
}

// ──────────────────────────────────────────────
//  GENERIC ENTITY CLIENT  →  /api/entities/:entity
//  Matches the backend generic CRUD layer. `list`/`filter` never throw so
//  react-query screens degrade gracefully; writes throw so mutations surface
//  real errors to the UI (toasts).
// ──────────────────────────────────────────────
function createEntityClient(entityName) {
  const base = `/entities/${entityName}`;
  return {
    list: async (params = {}) => {
      const query = new URLSearchParams(params).toString();
      try {
        return await apiRequest(query ? `${base}?${query}` : base);
      } catch (err) {
        console.warn(`[HRMS] ${entityName}.list failed:`, err.message);
        return [];
      }
    },
    // Server-side search / sort / pagination for the admin console's data
    // grid. `params` may include: search, sort, order, limit, offset, plus
    // any column the backend whitelists as filterable for this entity
    // (status, department, role, priority, …). Returns { rows, total }.
    page: async (params = {}) => {
      const clean = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
      );
      const query = new URLSearchParams(clean).toString();
      try {
        return await apiRequestWithTotal(query ? `${base}?${query}` : base);
      } catch (err) {
        console.warn(`[HRMS] ${entityName}.page failed:`, err.message);
        return { rows: [], total: 0 };
      }
    },
    get: async (id) => {
      try {
        return await apiRequest(`${base}/${id}`);
      } catch (err) {
        return null;
      }
    },
    filter: async (criteria = {}) => {
      let all = [];
      try {
        all = await apiRequest(base);
      } catch (err) {
        return [];
      }
      return (Array.isArray(all) ? all : []).filter((item) =>
        Object.entries(criteria).every(([key, value]) => item[key] === value)
      );
    },
    create: (data) => apiRequest(base, { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`${base}/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (id, data) =>
      apiRequest(`${base}/${id}`, {
        method: "DELETE",
        ...(data ? { body: JSON.stringify(data) } : {}),
      }),  };
}

// ──────────────────────────────────────────────
//  PUBLIC API, talks to the WebIdeal HRMS backend
// ──────────────────────────────────────────────
export const backendApi = {
  auth: {
    getToken: () => accessToken,
    getStoredUser: () => currentUser,
    // Permission keys for the signed-in user, populated by me(). Call me()
    // first; this is a synchronous read of what that last call returned.
    getPermissions: () => currentPermissions,

    isAuthenticated: () => !!accessToken,

    // Call once when the app boots (before rendering the dashboard/landing
    // page). Exchanges the httpOnly refresh cookie, if the browser has one
    //, for a fresh access token. This is what makes "remember me" actually
    // keep someone logged in across a browser restart: there's nothing in
    // localStorage to check, the server tells us via the cookie.
    restoreSession: () => silentRefresh(),

    // Returns the current user or null. Never throws; falls back to the
    // last known user on a transient failure so a backend hiccup doesn't
    // log someone out.
    me: async () => {
      if (!accessToken) return null;
      try {
        const raw = await apiRequest("/auth/me");
        const user = normalizeUser(raw?.user || raw);
        currentUser = user;
        // The server resolves role -> permissions and ships the list, so the
        // console gates its own buttons off the same grants the API
        // enforces instead of re-deriving them from hard-coded role arrays.
        currentPermissions = Array.isArray(raw?.permissions) ? raw.permissions : [];
        return user;
      } catch {
        return currentUser;
      }
    },

    // rememberMe is sent to the backend and decided there, see
    // Backend/src/services/auth.service.js. This client never stores the
    // credential; it only ever gets an in-memory access token back.
    login: async (email, password, rememberMe = false) => {
      const raw = await apiRequest("/auth/login", {
        method: "POST",
        // Normalize email (trim + lowercase) so copy-paste whitespace or
        // capitalization doesn't cause a false 401.
        body: JSON.stringify({
          email: (email || "").trim().toLowerCase(),
          password,
          remember_me: !!rememberMe,
        }),
      });
      const token = raw.token || raw.accessToken || raw.jwt;
      if (!token) throw new Error("Login response missing token");
      const user = normalizeUser(raw.user || raw);
      setSession(token, user);
      return user;
    },

    // Legacy alias: the backend now treats /register exactly like
    // /request-access (admin approval required, no instant account or
    // token), so there's nothing to store here.
    register: async ({ full_name, email }) => {
      const raw = await apiRequest("/auth/register", {
        method: "POST",
        body: JSON.stringify({ full_name, email: (email || "").trim().toLowerCase() }),
      });
      return normalizeUser(raw.user || raw);
    },

    // Invite-based flow: prospective user asks for access and the backend
    // auto-detects which company's admin this should route to, based on the
    // requester's email domain. No company list/dropdown involved, if the
    // domain can't be matched, the backend returns a clear error asking the
    // person to use their correct company email address.
    requestAccess: ({ full_name, email }) =>
      apiRequest("/auth/request-access", {
        method: "POST",
        body: JSON.stringify({ full_name, email }),
      }),

    // Set password from an emailed invitation token; activates the account
    // and logs the person straight in (session-only, they can tick
    // "remember me" the next time they log in normally).
    setPassword: async ({ token, password }) => {
      const raw = await apiRequest("/auth/set-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      const jwt = raw.token || raw.accessToken;
      const user = normalizeUser(raw.user || raw);
      if (jwt) setSession(jwt, user);
      return user;
    },

    // Ends the session on THIS device: tells the backend to revoke the
    // refresh token and clear its cookie, then drops the in-memory access
    // token. Network failures still clear the local session immediately, // the UI should never appear "stuck" logged in.
    logout: async () => {
      try {
        await rawRequest("/auth/logout", { method: "POST" });
      } catch {
        // best-effort, proceed to clear the local session regardless
      } finally {
        clearSession();
      }
    },

    // "Sign out everywhere": revokes every refresh token for this user
    // (every device/browser that was remembered), not just this one.
    logoutAllDevices: async () => {
      try {
        await apiRequest("/auth/logout-all", { method: "POST" });
      } finally {
        clearSession();
      }
    },

    // Self-service profile update from Settings: name, email, and/or
    // password (current_password required whenever new_password is set).
    updateProfile: async ({ full_name, email, current_password, new_password }) => {
      const body = JSON.stringify({ full_name, email, current_password, new_password });
      let raw;
      try {
        raw = await apiRequest("/auth/me", { method: "PUT", body });
      } catch (err) {
        // Some hosting/proxy setups block or mangle PUT before it reaches the
        // API, which surfaces as a routing-style 404. The backend accepts
        // POST on the same endpoint as a fallback, retry with that instead
        // of failing the whole update.
        if (err.status === 404) {
          raw = await apiRequest("/auth/me", { method: "POST", body });
        } else {
          throw err;
        }
      }
      const user = normalizeUser(raw?.user || raw);
      if (user) currentUser = user;
      return user;
    },

    // Admin: list pending access requests → { requests: [...] }
    listAccessRequests: async () => {
      try {
        const res = await apiRequest("/auth/access-requests");
        return res?.requests || [];
      } catch {
        return [];
      }
    },

    // Admin approve/reject. These backend endpoints are GET links (they render
    // an HTML confirmation page) but performing the request still applies the
    // action, so we call them directly and treat a 2xx as success.
    approveAccessRequest: async (id) => {
      const res = await fetch(`${API_BASE}/auth/approve-via-email/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Approve failed: ${res.status}`);
      return { ok: true };
    },
    rejectAccessRequest: async (id) => {
      const res = await fetch(`${API_BASE}/auth/reject-via-email/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Reject failed: ${res.status}`);
      return { ok: true };
    },
  },

  // Punch-in/out attendance for the logged-in user (/api/attendance)
  attendance: {
    punchIn: () => apiRequest("/attendance/punch-in", { method: "POST" }),
    punchOut: () => apiRequest("/attendance/punch-out", { method: "POST" }),
    today: () => apiRequest("/attendance/today"),
    history: async (params = {}) => {
      const query = new URLSearchParams(params).toString();
      try {
        const res = await apiRequest(query ? `/attendance/history?${query}` : "/attendance/history");
        return res?.attendance || [];
      } catch {
        return [];
      }
    },
  },

  // ──────────────────────────────────────────────
  //  PAYROLL  →  /api/payroll
  //
  //  Computation lives on the server, not the client: the client never adds
  //  up a payslip. Reads degrade to an empty value so a tab renders "no
  //  data" instead of blanking; every write throws, because a payroll
  //  mutation that silently failed is the worst possible outcome here.
  // ──────────────────────────────────────────────
  payroll: {
    /* ── Preparing and closing a period ─────────────────────────── */

    // Computes the register WITHOUT writing anything, so an administrator
    // can check the numbers before committing to them.
    preview: async ({ month, year } = {}) => {
      const params = new URLSearchParams();
      if (month) params.set("month", month);
      if (year) params.set("year", year);
      const query = params.toString();
      try {
        return await apiRequest(`/payroll/preview${query ? `?${query}` : ""}`);
      } catch (err) {
        console.warn("[HRMS] payroll.preview failed:", err.message);
        return null;
      }
    },

    run: ({ month, year } = {}) =>
      apiRequest("/payroll/run", { method: "POST", body: JSON.stringify({ month, year }) }),

    runs: async ({ limit } = {}) => {
      const query = limit ? `?limit=${limit}` : "";
      try {
        const res = await apiRequest(`/payroll/runs${query}`);
        return Array.isArray(res) ? res : res?.runs || res?.rows || [];
      } catch (err) {
        console.warn("[HRMS] payroll.runs failed:", err.message);
        return [];
      }
    },

    getRun: async (id) => {
      try {
        return await apiRequest(`/payroll/runs/${id}`);
      } catch (err) {
        console.warn("[HRMS] payroll.getRun failed:", err.message);
        return null;
      }
    },

    // Locks the run. After this the server refuses to change any figure in
    // it — the database enforces that, not just the API.
    approve: (id) => apiRequest(`/payroll/runs/${id}/approve`, { method: "POST" }),

    markPaid: (id, { payDate } = {}) =>
      apiRequest(`/payroll/runs/${id}/pay`, {
        method: "POST",
        body: JSON.stringify({ payDate }),
      }),

    cancel: (id, { reason } = {}) =>
      apiRequest(`/payroll/runs/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),

    /* ── Payslips ───────────────────────────────────────────────── */

    payslip: async (id) => {
      try {
        return await apiRequest(`/payroll/payslips/${id}`);
      } catch (err) {
        console.warn("[HRMS] payroll.payslip failed:", err.message);
        return null;
      }
    },

    myPayslips: async ({ limit } = {}) => {
      const query = limit ? `?limit=${limit}` : "";
      try {
        const res = await apiRequest(`/payroll/my-payslips${query}`);
        return Array.isArray(res) ? res : res?.payslips || res?.rows || [];
      } catch (err) {
        console.warn("[HRMS] payroll.myPayslips failed:", err.message);
        return [];
      }
    },

    monthlySummary: async () => {
      try {
        const res = await apiRequest("/payroll/monthly-summary");
        return Array.isArray(res) ? res : res?.rows || [];
      } catch {
        return [];
      }
    },

    /* ── Configuration ──────────────────────────────────────────── */

    statutory: async () => {
      try {
        return await apiRequest("/payroll/statutory");
      } catch (err) {
        console.warn("[HRMS] payroll.statutory failed:", err.message);
        return null;
      }
    },

    updateStatutory: (payload) =>
      apiRequest("/payroll/statutory", { method: "PUT", body: JSON.stringify(payload) }),

    structures: async () => {
      try {
        const res = await apiRequest("/payroll/structures");
        return Array.isArray(res) ? res : res?.structures || res?.rows || [];
      } catch (err) {
        console.warn("[HRMS] payroll.structures failed:", err.message);
        return [];
      }
    },

    structure: async (id) => {
      try {
        return await apiRequest(`/payroll/structures/${id}`);
      } catch (err) {
        console.warn("[HRMS] payroll.structure failed:", err.message);
        return null;
      }
    },

    setStructureComponents: (id, components) =>
      apiRequest(`/payroll/structures/${id}/components`, {
        method: "PUT",
        body: JSON.stringify({ components }),
      }),

    setDefaultStructure: (id) =>
      apiRequest(`/payroll/structures/${id}/default`, { method: "POST" }),

    /* ── Employee salary ────────────────────────────────────────── */

    // Who has a salary structure assigned and who is still missing one —
    // the gap that would otherwise surface as a zero on a payslip.
    salaryCoverage: async () => {
      try {
        return await apiRequest("/payroll/salary/coverage");
      } catch (err) {
        console.warn("[HRMS] payroll.salaryCoverage failed:", err.message);
        return null;
      }
    },

    salaryHistory: async (userId) => {
      try {
        const res = await apiRequest(`/payroll/salary/${userId}`);
        return Array.isArray(res) ? res : res?.history || res?.rows || [];
      } catch (err) {
        console.warn("[HRMS] payroll.salaryHistory failed:", err.message);
        return [];
      }
    },

    // Assigning a new salary does not overwrite the previous one — it
    // closes it, so historical payslips stay explainable.
    assignSalary: (userId, payload) =>
      apiRequest(`/payroll/salary/${userId}`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),

    confirmImportedSalary: (userId) =>
      apiRequest(`/payroll/salary/${userId}/confirm-import`, { method: "POST" }),
  },

  // Leave balances & aggregates computed server-side.
  leave: {
    balance: async () => {
      try {
        return await apiRequest("/leave/balance");
      } catch {
        return [];
      }
    },
    monthlySummary: async () => {
      try {
        return await apiRequest("/leave/monthly-summary");
      } catch {
        return [];
      }
    },
  },

  // Real-time in-app notifications (dashboard bell). `stream()` opens a
  // Server-Sent Events connection so new notifications arrive instantly
  // without polling; callers should still poll occasionally as a fallback
  // for networks/proxies that block long-lived SSE connections.
  notifications: {
    list: async (params = {}) => {
      const query = new URLSearchParams(params).toString();
      try {
        const res = await apiRequest(query ? `/notifications?${query}` : "/notifications");
        return res?.notifications || [];
      } catch {
        return [];
      }
    },
    unreadCount: async () => {
      try {
        const res = await apiRequest("/notifications/unread-count");
        return res?.count || 0;
      } catch {
        return 0;
      }
    },
    markRead: (id) => apiRequest(`/notifications/${id}/read`, { method: "PATCH" }),
    markAllRead: () => apiRequest("/notifications/mark-all-read", { method: "POST" }),

    // Opens an EventSource against /notifications/stream. Auth travels as a
    // `?token=` query param since EventSource can't send custom headers.
    // Returns the EventSource (or null if there's no session) so the caller
    // can add listeners and close() it on unmount.
    //
    // Note: this uses the short-lived (15 min) access token, since
    // EventSource has no hook to refresh it mid-connection, the stream
    // will need to be re-opened (e.g. on reconnect/tab focus) after that if
    // it's ever open longer than that.
    stream: () => {
      if (!accessToken || typeof EventSource === "undefined") return null;
      return new EventSource(`${API_BASE}/notifications/stream?token=${encodeURIComponent(accessToken)}`);
    },
  },

  // Generic HR entities backed by /api/entities/:entity
  //
  // Every entity a page reads through `entities.X` has to be registered
  // here. An unregistered name is not a missing feature, it is `undefined`:
  // the page then throws on `.list()` at first render rather than showing an
  // empty state, which is why the configuration screens below are grouped
  // and kept alongside the records they configure.
  entities: {
    // Records
    Employee: createEntityClient("Employee"),
    Attendance: createEntityClient("Attendance"),
    LeaveRequest: createEntityClient("LeaveRequest"),
    Performance: createEntityClient("Performance"),
    Candidate: createEntityClient("Candidate"),
    Document: createEntityClient("Document"),
    Payroll: createEntityClient("Payroll"),
    Ticket: createEntityClient("Ticket"),

    // Leave configuration, read by LeaveSetupPage
    LeaveType: createEntityClient("LeaveType"),
    LeavePolicy: createEntityClient("LeavePolicy"),
    Holiday: createEntityClient("Holiday"),

    // Org structure, read by OrganizationPage
    Location: createEntityClient("Location"),
    Department: createEntityClient("Department"),
    Designation: createEntityClient("Designation"),
    Grade: createEntityClient("Grade"),

    // Shift patterns and rosters, read by ShiftsPage
    Shift: createEntityClient("Shift"),
    ShiftAssignment: createEntityClient("ShiftAssignment"),
  },

  // AI Assistant → server-side LLM proxy (key stays on the backend)
  integrations: {
    Core: {
      InvokeLLM: async ({ prompt }) => {
        return apiRequest("/integrations/invoke-llm", {
          method: "POST",
          body: JSON.stringify({ prompt }),
        });
      },
    },
  },

  // Platform owner (superadmin), oversee & provision all client tenants.
  superadmin: {
    overview: () => apiRequest("/superadmin/overview"),
    listTenants: () => apiRequest("/superadmin/tenants"),
    getTenant: (id) => apiRequest(`/superadmin/tenants/${id}`),
    createTenant: (data) =>
      apiRequest("/superadmin/tenants", { method: "POST", body: JSON.stringify(data) }),
    updateTenant: (id, data) =>
      apiRequest(`/superadmin/tenants/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    deleteTenant: (id) =>
      apiRequest(`/superadmin/tenants/${id}`, { method: "DELETE" }),
    addUser: (tenantId, data) =>
      apiRequest(`/superadmin/tenants/${tenantId}/users`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    // Removing a user from a client no longer deletes immediately, it
    // raises a pending request that the client's own admin must approve.
    requestUserDeletion: (tenantId, userId) =>
      apiRequest(`/superadmin/tenants/${tenantId}/users/${userId}/delete-request`, {
        method: "POST",
      }),
  },

  // Platform owner (superadmin), create & manage invoices for any client.
  invoices: {
    list: async (params = {}) => {
      const query = new URLSearchParams(params).toString();
      try {
        return await apiRequest(query ? `/superadmin/invoices?${query}` : "/superadmin/invoices");
      } catch (err) {
        console.warn("[HRMS] invoices.list failed:", err.message);
        return [];
      }
    },
    get: (id) => apiRequest(`/superadmin/invoices/${id}`),
    create: (data) =>
      apiRequest("/superadmin/invoices", { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) =>
      apiRequest(`/superadmin/invoices/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    remove: (id) => apiRequest(`/superadmin/invoices/${id}`, { method: "DELETE" }),
    // Read-only look-ahead at the number a *new* invoice would get, so the
    // editor can show it before the person has saved anything.
    nextNumber: (year) =>
      apiRequest(`/superadmin/invoices/next-number${year ? `?year=${year}` : ""}`),
  },

  // Client-admin side of the user-deletion approval workflow: pending
  // requests the superadmin has raised for this organization's users.
  deletionRequests: {
    list: async () => {
      try {
        const res = await apiRequest("/deletion-requests");
        return res?.requests || [];
      } catch (err) {
        console.warn("[HRMS] deletionRequests.list failed:", err.message);
        return [];
      }
    },
    approve: (id) => apiRequest(`/deletion-requests/${id}/approve`, { method: "POST" }),
    reject: (id) => apiRequest(`/deletion-requests/${id}/reject`, { method: "POST" }),
  },

  // ──────────────────────────────────────────────
  //  ADMIN CONSOLE  →  /api/admin
  //
  //  Org-wide aggregates computed server-side. Reads return a safe empty
  //  value on failure so a dashboard tile degrades to "no data" instead of
  //  collapsing the whole screen; the leave decision throws, because a
  //  mutation that silently did nothing would be worse than an error toast.
  // ──────────────────────────────────────────────
  admin: {
    // One call backing the whole Overview screen: headcount, attendance,
    // payroll, approvals, departments and trends.
    overview: async () => {
      try {
        return await apiRequest("/admin/overview");
      } catch (err) {
        console.warn("[HRMS] admin.overview failed:", err.message);
        return null;
      }
    },

    headcount: async () => {
      try {
        return await apiRequest("/admin/headcount");
      } catch (err) {
        console.warn("[HRMS] admin.headcount failed:", err.message);
        return null;
      }
    },

    headcountTrend: async (months) => {
      const query = months ? `?months=${months}` : "";
      try {
        return await apiRequest(`/admin/headcount/trend${query}`);
      } catch (err) {
        console.warn("[HRMS] admin.headcountTrend failed:", err.message);
        return [];
      }
    },

    departments: async () => {
      try {
        const res = await apiRequest("/admin/departments");
        return Array.isArray(res) ? res : res?.departments || [];
      } catch (err) {
        console.warn("[HRMS] admin.departments failed:", err.message);
        return [];
      }
    },

    attendanceSnapshot: async (date) => {
      const query = date ? `?date=${encodeURIComponent(date)}` : "";
      try {
        return await apiRequest(`/admin/attendance/snapshot${query}`);
      } catch (err) {
        console.warn("[HRMS] admin.attendanceSnapshot failed:", err.message);
        return null;
      }
    },

    attendanceTrend: async (days) => {
      const query = days ? `?days=${days}` : "";
      try {
        const res = await apiRequest(`/admin/attendance/trend${query}`);
        return Array.isArray(res) ? res : [];
      } catch (err) {
        console.warn("[HRMS] admin.attendanceTrend failed:", err.message);
        return [];
      }
    },

    // The day's attendance register — one row per employee.
    dailyAttendance: async (date) => {
      const query = date ? `?date=${encodeURIComponent(date)}` : "";
      try {
        const res = await apiRequest(`/admin/attendance/daily${query}`);
        return Array.isArray(res) ? res : res?.rows || [];
      } catch (err) {
        console.warn("[HRMS] admin.dailyAttendance failed:", err.message);
        return [];
      }
    },

    payrollSnapshot: async () => {
      try {
        return await apiRequest("/admin/payroll/snapshot");
      } catch (err) {
        console.warn("[HRMS] admin.payrollSnapshot failed:", err.message);
        return null;
      }
    },

    payrollTrend: async (months) => {
      const query = months ? `?months=${months}` : "";
      try {
        const res = await apiRequest(`/admin/payroll/trend${query}`);
        return Array.isArray(res) ? res : [];
      } catch (err) {
        console.warn("[HRMS] admin.payrollTrend failed:", err.message);
        return [];
      }
    },

    // Recent-events strip on the Overview. `limit` caps how many events.
    activity: async (limit) => {
      const query = limit ? `?limit=${limit}` : "";
      try {
        const res = await apiRequest(`/admin/activity${query}`);
        return Array.isArray(res) ? res : [];
      } catch (err) {
        console.warn("[HRMS] admin.activity failed:", err.message);
        return [];
      }
    },

    // The filterable, paginated audit trail behind the Activity Log screen.
    // Returns { rows, total } — the total comes from X-Total-Count.
    audit: async ({ action, entityType, actorId, outcome, limit, offset } = {}) => {
      const clean = Object.fromEntries(
        Object.entries({
          action,
          entity_type: entityType,
          actor_id: actorId,
          outcome,
          limit,
          offset,
        }).filter(([, v]) => v !== undefined && v !== null && v !== "")
      );
      const query = new URLSearchParams(clean).toString();
      try {
        return await apiRequestWithTotal(`/admin/audit${query ? `?${query}` : ""}`);
      } catch (err) {
        console.warn("[HRMS] admin.audit failed:", err.message);
        return { rows: [], total: 0 };
      }
    },

    // Full history of a single record, for the drawer on that record.
    entityHistory: async (entityType, id) => {
      try {
        const res = await apiRequest(`/admin/audit/${entityType}/${id}`);
        return Array.isArray(res) ? res : [];
      } catch (err) {
        console.warn("[HRMS] admin.entityHistory failed:", err.message);
        return [];
      }
    },

    report: async (type, { from, to } = {}) => {
      const clean = Object.fromEntries(
        Object.entries({ from, to }).filter(([, v]) => v !== undefined && v !== null && v !== "")
      );
      const query = new URLSearchParams(clean).toString();
      try {
        return await apiRequest(`/admin/reports/${type}${query ? `?${query}` : ""}`);
      } catch (err) {
        console.warn("[HRMS] admin.report failed:", err.message);
        return null;
      }
    },

    // The approval queue and its badge counts.
    approvals: async () => {
      try {
        const res = await apiRequest("/admin/approvals");
        return Array.isArray(res) ? res : res?.rows || [];
      } catch (err) {
        console.warn("[HRMS] admin.approvals failed:", err.message);
        return [];
      }
    },

    approvalCounts: async () => {
      try {
        return await apiRequest("/admin/approvals/counts");
      } catch (err) {
        console.warn("[HRMS] admin.approvalCounts failed:", err.message);
        return null;
      }
    },

    // `decision` is "approve" or "reject". Throws so the UI can surface the
    // real reason (e.g. "you are not an approver for this step").
    decideLeave: (id, decision) =>
      apiRequest(`/admin/approvals/leave/${id}`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      }),
  },

  // ──────────────────────────────────────────────
  //  APPROVAL ENGINE  →  /api/approvals
  //
  //  The generic multi-step workflow (leave, account removal, …). Whether
  //  the caller may decide a given request depends on the policy step it
  //  is sitting on, so the server decides that per request.
  // ──────────────────────────────────────────────
  approvals: {
    // Paginated/filterable list for the Approvals screen → { rows, total }.
    page: async (params = {}) => {
      const clean = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
      );
      const query = new URLSearchParams(clean).toString();
      try {
        return await apiRequestWithTotal(`/approvals${query ? `?${query}` : ""}`);
      } catch (err) {
        console.warn("[HRMS] approvals.page failed:", err.message);
        return { rows: [], total: 0 };
      }
    },

    // What is waiting on *me* specifically.
    inbox: async () => {
      try {
        const res = await apiRequest("/approvals/inbox");
        return Array.isArray(res) ? res : res?.rows || [];
      } catch (err) {
        console.warn("[HRMS] approvals.inbox failed:", err.message);
        return [];
      }
    },

    counts: async () => {
      try {
        return await apiRequest("/approvals/counts");
      } catch (err) {
        console.warn("[HRMS] approvals.counts failed:", err.message);
        return null;
      }
    },

    policies: async () => {
      try {
        const res = await apiRequest("/approvals/policies");
        return Array.isArray(res) ? res : res?.rows || [];
      } catch (err) {
        console.warn("[HRMS] approvals.policies failed:", err.message);
        return [];
      }
    },

    get: (id) => apiRequest(`/approvals/${id}`),

    decide: (id, decision, note) =>
      apiRequest(`/approvals/${id}/decide`, {
        method: "POST",
        body: JSON.stringify({ decision, note }),
      }),

    cancel: (id, reason) =>
      apiRequest(`/approvals/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
  },

  // ──────────────────────────────────────────────
  //  ONBOARDING  →  /api/onboarding
  //
  //  Admins invite new hires, review what they submit, and approve (which
  //  creates the account and emails credentials) or reject. All of these
  //  require the `org.manage` permission server-side.
  // ──────────────────────────────────────────────
  onboarding: {
    listInvites: async ({ status } = {}) => {
      const query = status ? `?status=${encodeURIComponent(status)}` : "";
      try {
        const res = await apiRequest(`/onboarding/admin/invites${query}`);
        return res?.invites || [];
      } catch (err) {
        console.warn("[HRMS] onboarding.listInvites failed:", err.message);
        return [];
      }
    },

    createInvite: (data) =>
      apiRequest("/onboarding/admin/invites", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    listSubmissions: async ({ status = "submitted" } = {}) => {
      const query = `?status=${encodeURIComponent(status)}`;
      try {
        const res = await apiRequest(`/onboarding/admin/submissions${query}`);
        return res?.submissions || [];
      } catch (err) {
        console.warn("[HRMS] onboarding.listSubmissions failed:", err.message);
        return [];
      }
    },

    getSubmission: (id) => apiRequest(`/onboarding/admin/submissions/${id}`),

    approveSubmission: (id, body = {}) =>
      apiRequest(`/onboarding/admin/submissions/${id}/approve`, {
        method: "POST",
        body: JSON.stringify(body),
      }),

    rejectSubmission: (id, reason) =>
      apiRequest(`/onboarding/admin/submissions/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),

    stats: async () => {
      try {
        const res = await apiRequest("/onboarding/admin/stats");
        return res?.stats || null;
      } catch (err) {
        console.warn("[HRMS] onboarding.stats failed:", err.message);
        return null;
      }
    },
  },
};

export default backendApi;
